import type { RouterProps } from 'wouter';
import { DashboardLayout } from 'components/Layout/DashboardLayout';
import { CreateInvoicePage } from 'components/pages/payments/CreateInvoicePage';
import { ExternalPaymentDetailPage } from 'components/pages/payments/ExternalPaymentDetailPage';
import { InvoiceDetailPage } from 'components/pages/payments/InvoiceDetailPage/index';
import { InvoicesListPage } from 'components/pages/payments/InvoicesListPage';
import { PartiesListPage } from 'components/pages/payments/PartiesListPage';
import { PartyDetailPage } from 'components/pages/payments/PartyDetailPage';
import { PaymentsListPage } from 'components/pages/payments/PaymentsListPage';
import { ProductListPage } from 'components/pages/payments/ProductListPage';
import { PublicInvoiceViewPage } from 'components/pages/payments/PublicInvoiceViewPage';
import { PublicPartyViewPage } from 'components/pages/payments/PublicPartyViewPage';
import { PaymentsHomePage } from 'components/pages/PaymentsHomePage/PaymentsHomePage';
import React, { Suspense } from 'react';
import { Route, Router, Switch } from 'wouter';
import { usePaymentsApiHttpClientContext } from 'wrappers/PaymentsApiAuthProvider';
import { PlatformEntityProvider } from 'wrappers/PlatformEntityProvider';

import { DecodedParamsRoute } from './DecodedParamsRoute';

export const PaymentsRouter = ({
  hook,
  base,
  workspaceId,
}: Partial<RouterProps> & { workspaceId: string }): ReturnType<
  typeof Router
> => {
  const { hasWorkspace } = usePaymentsApiHttpClientContext();
  if (!hasWorkspace) {
    return (
      <DashboardLayout>
        <>Loading...</>
      </DashboardLayout>
    );
  }
  return (
    <PlatformEntityProvider>
      <DashboardLayout>
        <Suspense fallback={<>Loading...</>}>
          <Router hook={hook} base={base} key={base}>
            <Switch>
              <DecodedParamsRoute path="/parties/:externalId/invoices/:invoiceId">
                {(params) => (
                  <PublicInvoiceViewPage
                    workspaceId={workspaceId}
                    partyExternalId={params.externalId}
                    invoiceId={params.invoiceId}
                  />
                )}
              </DecodedParamsRoute>
              <DecodedParamsRoute path="/parties/:externalId/view">
                {(params) => (
                  <PublicPartyViewPage
                    workspaceId={workspaceId}
                    externalId={params.externalId}
                  />
                )}
              </DecodedParamsRoute>
              <DecodedParamsRoute path="/parties/:externalId">
                {(params) => (
                  <PartyDetailPage
                    workspaceId={workspaceId}
                    externalId={params.externalId}
                  />
                )}
              </DecodedParamsRoute>
              <DecodedParamsRoute path="/parties">
                <PartiesListPage workspaceId={workspaceId} />
              </DecodedParamsRoute>
              <DecodedParamsRoute path="/invoices/:invoiceId">
                {(params) => (
                  <InvoiceDetailPage
                    workspaceId={workspaceId}
                    invoiceId={params.invoiceId}
                  />
                )}
              </DecodedParamsRoute>
              <DecodedParamsRoute path="/invoices">
                <InvoicesListPage workspaceId={workspaceId} />
              </DecodedParamsRoute>
              <DecodedParamsRoute path="/products">
                <ProductListPage workspaceId={workspaceId} />
              </DecodedParamsRoute>
              <DecodedParamsRoute path="/payments/:transactionId">
                {(params) => (
                  <ExternalPaymentDetailPage
                    workspaceId={workspaceId}
                    transactionId={params.transactionId}
                  />
                )}
              </DecodedParamsRoute>
              <DecodedParamsRoute path="/payments">
                <PaymentsListPage workspaceId={workspaceId} />
              </DecodedParamsRoute>
              <DecodedParamsRoute path="/create-invoice">
                <CreateInvoicePage workspaceId={workspaceId} />
              </DecodedParamsRoute>
              <DecodedParamsRoute path="/">
                <PaymentsHomePage />
              </DecodedParamsRoute>
              <Route />
            </Switch>
          </Router>
        </Suspense>
      </DashboardLayout>
    </PlatformEntityProvider>
  );
};
